import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useAuth } from '../AuthProvider'
import { Button } from '../ui/Button'
import { getActiveOrgId } from '../../utils/orgUtils'
import { acceptOrgView } from '../../utils/orgContext.mjs'
import { buildCloudRequestUrl, cloudFailureCode } from './gcpDiscoverySession.mjs'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

type Relation = {
  type: string
  resourceId: string
  name?: string | null
  kind: string
  externalId?: string
  sameNetworkOnly?: boolean
  observedTraffic?: boolean
}
type Check = {
  ruleId: string
  ruleTitle?: string
  status: string
  reason?: string | null
  remediation?: string | null
  findingInstanceId?: string | null
  evidenceId?: string | null
}
type Workload = { type: string; name?: string | null; image?: string | null }
type Addresses = { privateIpv4?: string[]; publicIpv4?: string[]; publicIpv6?: string[] }
type Unresolved = { type: string; reference: string }
type OsConfigSummary = {
  observationId?: string | null
  sourceId?: string | null
  observedAt?: string | null
  sourceType?: string | null
  os?: { name?: string | null; version?: string | null } | null
}
type PackageEntry = { name?: string | null; version?: string | null }
type PackageView = { packages?: PackageEntry[]; updates?: PackageEntry[] }

const API = import.meta.env.VITE_API_URL || '/api'

function formatWhen(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString()
}

const GROUPS = [
  { id: 'disks', types: ['ATTACHED_DISK'], labelKey: 'gcpIntegration.disks', fallback: 'Disks' },
  { id: 'network', types: ['USES_NETWORK', 'USES_SUBNETWORK'], labelKey: 'gcpIntegration.networkGroup', fallback: 'Network and subnets' },
  { id: 'addresses', types: ['USES_ADDRESS'], labelKey: 'gcpIntegration.addresses', fallback: 'IP addresses' },
  { id: 'firewalls', types: ['CONFIGURED_ON_SAME_NETWORK'], labelKey: 'gcpIntegration.firewalls', fallback: 'Firewall rules' },
] as const

function reasonText(t: (key: string, fallback?: string, params?: Record<string, string | number>) => string, reason?: string | null) {
  if (!reason) return t('gcpChecks.reasonMissing', 'The required data was not collected.')
  const known = t(`gcpChecks.reasons.${reason}`, '')
  if (known && known !== `gcpChecks.reasons.${reason}`) return known
  return t('gcpChecks.reasonGeneric', 'The check stopped because this data is missing: {{reason}}.', { reason })
}

export default function CloudAssetTechnicalPanel({ assetId }: { assetId: string }) {
  const { t } = useI18n()
  const { user, token } = useAuth()
  const userId = String(user?.id || '')
  const [orgId, setOrgId] = useState<string | null>(() => getActiveOrgId())
  const [relations, setRelations] = useState<Relation[] | null>(null)
  const [checks, setChecks] = useState<Check[]>([])
  const [workloads, setWorkloads] = useState<Workload[]>([])
  const [addresses, setAddresses] = useState<Addresses>({})
  const [unresolved, setUnresolved] = useState<Unresolved[]>([])
  const [osConfig, setOsConfig] = useState<OsConfigSummary | null>(null)
  const [integrationId, setIntegrationId] = useState<string | null>(null)
  const [packageView, setPackageView] = useState<PackageView | null>(null)
  const [packagesOpen, setPackagesOpen] = useState(false)
  const [packageError, setPackageError] = useState(false)
  const [denied, setDenied] = useState(false)
  const viewRef = useRef({ generation: 0, userId, orgId })

  useEffect(() => {
    const apply = (next: string | null) => setOrgId((prev) => (prev === next ? prev : next))
    const onOrg = (event: Event) => {
      const detail = (event as CustomEvent<{ organizationId?: string | null }>).detail
      apply(detail && 'organizationId' in detail ? detail.organizationId || null : getActiveOrgId())
    }
    window.addEventListener('ds-active-organization-changed', onOrg)
    return () => window.removeEventListener('ds-active-organization-changed', onOrg)
  }, [])

  useEffect(() => {
    viewRef.current = { generation: viewRef.current.generation + 1, userId, orgId }
    setRelations(null)
    setOsConfig(null)
    setIntegrationId(null)
    setPackageView(null)
    setPackagesOpen(false)
    setPackageError(false)
    setDenied(false)
    if (!token || !userId || !orgId || !assetId) return
    const ticket = { ...viewRef.current }
    void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || '/api', `/asset-links/${assetId}`, orgId), {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (response) => {
      const body = await response.json().catch(() => ({}))
      if (!acceptOrgView(ticket, viewRef.current)) return
      if (cloudFailureCode(response.status, body) === 'ORGANIZATION_PERMISSION_DENIED') {
        setDenied(true)
        setRelations(null)
        return
      }
      if (!response.ok || body.linked === false) {
        setRelations([])
        setChecks([])
        setWorkloads([])
        setAddresses({})
        setUnresolved([])
        setOsConfig(null)
        setIntegrationId(null)
        return
      }
      setRelations(Array.isArray(body.relations) ? body.relations : [])
      setChecks(Array.isArray(body.checks) ? body.checks : [])
      setWorkloads(Array.isArray(body.workloads) ? body.workloads : [])
      setAddresses(body.addresses && typeof body.addresses === 'object' ? body.addresses : {})
      setUnresolved(Array.isArray(body.unresolved) ? body.unresolved : [])
      setIntegrationId(typeof body.integrationId === 'string' ? body.integrationId : null)
      setOsConfig(body.osConfig && body.osConfig.sourceType === 'OS_CONFIG' ? body.osConfig : null)
    }).catch(() => {
      if (!acceptOrgView(ticket, viewRef.current)) return
      setRelations([])
      setChecks([])
      setWorkloads([])
      setAddresses({})
      setUnresolved([])
      setOsConfig(null)
      setIntegrationId(null)
    })
  }, [assetId, orgId, token, userId])

  async function openPackages() {
    if (!token || !orgId || !integrationId || !osConfig?.sourceId || !osConfig.observationId) return
    setPackageError(false)
    if (packagesOpen) {
      setPackagesOpen(false)
      setPackageView(null)
      return
    }
    try {
      const response = await fetch(buildCloudRequestUrl(API, `/${integrationId}/sources/${osConfig.sourceId}/observations/${osConfig.observationId}`, orgId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok || body.sourceType !== 'OS_CONFIG') {
        setPackageView(null)
        setPackagesOpen(false)
        setPackageError(true)
        return
      }
      setPackageView({
        packages: Array.isArray(body.packages) ? body.packages : [],
        updates: Array.isArray(body.updates) ? body.updates : [],
      })
      setPackagesOpen(true)
    } catch {
      setPackageView(null)
      setPackagesOpen(false)
      setPackageError(true)
    }
  }

  if (denied) {
    return <p data-testid="cloud-asset-permission">{t('integrations.overview.permission', 'You do not have permission to view one or more integration sources.')}</p>
  }

  const rows = relations || []
  const findings = checks.filter((check) => check.status === 'FAIL')
  const gaps = checks.filter((check) => check.status === 'NOT_EVALUATED')
  const addressLines = [
    ...(addresses.privateIpv4 || []).map((value) => `${t('gcpIntegration.privateIpv4', 'Private IPv4')}: ${value}`),
    ...(addresses.publicIpv4 || []).map((value) => `${t('gcpIntegration.publicIpv4', 'Public IPv4')}: ${value}`),
    ...(addresses.publicIpv6 || []).map((value) => `${t('gcpIntegration.publicIpv6', 'Public IPv6')}: ${value}`),
  ]
  const osVersion = [osConfig?.os?.name, osConfig?.os?.version].filter(Boolean).join(' ')
  const hasOs = Boolean(osConfig?.observationId && osConfig?.sourceId)
  const hasContent = rows.length > 0 || findings.length > 0 || gaps.length > 0 || workloads.length > 0 || addressLines.length > 0 || unresolved.length > 0 || hasOs
  if (!relations || !hasContent) return null
  return (
    <section data-testid="cloud-asset-technical">
      <p>{t('gcpChecks.configNotScanner', 'These are configuration checks. They are not scanner vulnerabilities.')}</p>
      {findings.length > 0 ? (
        <>
          <h2>{t('gcpChecks.findings', 'Configuration findings')}</h2>
          <ul>
            {findings.map((check) => (
              <li key={`${check.ruleId}:${check.reason || ''}`}>
                <Link to="/compliance-center/automation">{check.ruleTitle || check.ruleId}</Link>
                {` · ${t('gcpChecks.fail', 'Fail')}`}
                {check.findingInstanceId ? ` · ${t('gcpChecks.linkedTask', 'Linked remediation task')}: ${check.findingInstanceId}` : ` · ${t('gcpChecks.noSpecificTask', 'No remediation task is linked to this check.')}`}
                {' · '}
                <Link to="/compliance-center/remediation">{t('gcpChecks.generalRemediation', 'Remediation workspace (general)')}</Link>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {gaps.length > 0 ? (
        <>
          <h2>{t('gcpChecks.gaps', 'Checks not evaluated')}</h2>
          <p>{t('gcpChecks.notVulnerability', 'This check was not evaluated. It is not a confirmed vulnerability or finding.')}</p>
          <ul>
            {gaps.map((check) => (
              <li key={`${check.ruleId}:${check.reason || ''}`}>
                {check.ruleTitle || check.ruleId}
                {` · ${reasonText(t, check.reason)}`}
                {` · ${t('gcpChecks.neededAction', 'What is needed')}: ${check.remediation || t('gcpChecks.reasonMissing', 'The required data was not collected.')}`}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {addressLines.length > 0 ? (
        <>
          <h2>{t('gcpIntegration.addresses', 'IP addresses')}</h2>
          <ul>{addressLines.map((line) => <li key={line}>{line}</li>)}</ul>
          <p>{t('gcpIntegration.scanTarget', 'These addresses come from the cloud inventory. They do not replace a scan target set on the asset.')}</p>
          <p>{t('gcpIntegration.noHostname', 'Hostname and URL were not collected. The resource name is not used as a hostname.')}</p>
        </>
      ) : null}
      {GROUPS.map((group) => {
        const members = rows.filter((row) => (group.types as readonly string[]).includes(row.type))
        if (members.length === 0) return null
        return (
          <div key={group.id}>
            <h2>{t(group.labelKey, group.fallback)}</h2>
            <div className={listStyles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>{t('gcpIntegration.name', 'Name')}</th>
                    <th>{t('gcpIntegration.kind', 'Kind')}</th>
                    <th>{t('gcpIntegration.relationCode', 'Relation code')}</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((relation) => (
                    <tr key={`${relation.type}:${relation.resourceId}`}>
                      <td>{relation.name || relation.kind}</td>
                      <td>{relation.kind}</td>
                      <td className={listStyles.cellMuted}>{relation.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {group.id === 'firewalls' ? (
              <>
                <p>{t('gcpIntegration.sameNetwork', 'This rule is on the same network. Targets were not checked, so it is not confirmed to apply to this VM. This is not observed traffic.')}</p>
                <p>{t('gcpIntegration.notHostFirewall', 'A VPC firewall rule does not confirm a host firewall, EDR, encryption, or patch management.')}</p>
              </>
            ) : null}
          </div>
        )
      })}
      {unresolved.length > 0 ? (
        <>
          <h2>{t('gcpIntegration.unresolvedTitle', 'Collected references not in this inventory')}</h2>
          <ul>
            {unresolved.map((item) => (
              <li key={`${item.type}:${item.reference}`}>{item.type}: {item.reference}</li>
            ))}
          </ul>
          <p>{t('gcpIntegration.unresolved', 'A collected reference is not in this organization\'s inventory. It is not linked by name.')}</p>
        </>
      ) : null}
      {hasOs ? (
        <div data-testid="cloud-asset-os-config">
          <h2>{t('gcpSources.observationSource', 'OS Config')}</h2>
          <div className={listStyles.tableWrap}>
            <table>
              <tbody>
                <tr>
                  <th scope="row">{t('gcpSources.osVersion', 'OS version')}</th>
                  <td>{osVersion || t('gcpSources.none', 'None')}</td>
                </tr>
                <tr>
                  <th scope="row">{t('gcpSources.source', 'Source')}</th>
                  <td>{t('gcpSources.observationSource', 'OS Config')}</td>
                </tr>
                <tr>
                  <th scope="row">{t('gcpSources.observed', 'Last observation')}</th>
                  <td>{formatWhen(osConfig?.observedAt) || t('gcpSources.none', 'None')}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <Button type="button" size="small" color="outline" onClick={() => void openPackages()}>
            {packagesOpen ? t('gcpSources.closePackages', 'Close package list') : t('gcpSources.packages', 'Packages')}
          </Button>
          {packageError ? <p>{t('gcpSources.requestFailed', 'The request did not complete.')}</p> : null}
          {packagesOpen && packageView ? (
            <div data-testid="cloud-asset-os-packages">
              <h3>{t('gcpSources.packages', 'Packages')}</h3>
              {(packageView.packages || []).length === 0 ? <p>{t('gcpSources.none', 'None')}</p> : (
                <ul>
                  {(packageView.packages || []).filter((entry) => typeof entry.name === 'string').map((entry) => (
                    <li key={`${entry.name}:${entry.version || ''}`}>{entry.name} {entry.version || ''}</li>
                  ))}
                </ul>
              )}
              <h3>{t('gcpSources.updates', 'Available updates')}</h3>
              {(packageView.updates || []).length === 0 ? <p>{t('gcpSources.none', 'None')}</p> : (
                <ul>
                  {(packageView.updates || []).filter((entry) => typeof entry.name === 'string').map((entry) => (
                    <li key={`update:${entry.name}:${entry.version || ''}`}>{entry.name} {entry.version || ''}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
      {workloads.length > 0 ? (
        <>
          <h2>{t('gcpSources.workloads', 'Workloads')}</h2>
          <ul>
            {workloads.map((item) => (
              <li key={`${item.name}:${item.image}`}>{item.name} · {item.image}</li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  )
}
